import { getAPI, patchAPI, postAPI } from "./Networking";
import { SwayyAPIResponse } from "../../../models/NetworkResponse";
import {
  CreateSwayyCouponPayload,
  GetSwayyCouponsParams,
  SwayyCoupon,
  SwayyCouponActivityLog,
  SwayyCouponBulkExpireResult,
  SwayyCouponRedemption,
  SwayyCouponsListData,
  UpdateSwayyCouponPayload,
} from "../../../models/SwayyCoupon/SwayyCoupon";
import {
  assertSuccess,
  getResponseData,
  normalizeSwayyApiError,
  requireRowArray,
} from "./swayyApiHelpers";

export default class SwayyCouponAPI {
  getQueryParams = (
    params: GetSwayyCouponsParams,
  ): Record<string, string | number> => {
    const offset = Math.max(0, (params.page - 1) * params.limit);
    const query: Record<string, string | number> = {
      limit: params.limit,
      offset,
      _ts: Date.now(),
    };
    if (params.status) query.status = params.status;
    if (params.q) query.q = params.q;
    if (params.channel) query.channel = params.channel;
    if (params.starts_from) query.starts_from = params.starts_from.slice(0, 10);
    if (params.starts_to) query.starts_to = params.starts_to.slice(0, 10);
    if (params.ends_from) query.ends_from = params.ends_from.slice(0, 10);
    if (params.ends_to) query.ends_to = params.ends_to.slice(0, 10);
    return query;
  };

  getCoupons = async (
    params: GetSwayyCouponsParams,
  ): Promise<SwayyCouponsListData> => {
    try {
      const response = (await getAPI(
        "/v1/cart/admin/coupons",
        this.getQueryParams(params),
      )) as SwayyAPIResponse<SwayyCoupon[]> & {
        pagination?: { total: number; limit: number; offset: number };
      };
      assertSuccess(response);
      const coupons = requireRowArray(
        response.data ?? response.result,
      ) as SwayyCoupon[];
      const total = response.pagination?.total ?? coupons.length;
      const limit = response.pagination?.limit ?? params.limit;
      const offset = response.pagination?.offset ?? 0;

      return {
        coupons,
        pagination: {
          total,
          page: Math.floor(offset / limit) + 1,
          limit,
        },
      };
    } catch (error) {
      throw normalizeSwayyApiError(error, "Failed to fetch coupons");
    }
  };

  getCoupon = async (couponId: number | string): Promise<SwayyCoupon> => {
    try {
      const response = (await getAPI(
        `/v1/cart/admin/coupons/${couponId}`,
      )) as SwayyAPIResponse<SwayyCoupon>;
      return getResponseData(assertSuccess(response));
    } catch (error) {
      throw normalizeSwayyApiError(error, "Failed to fetch coupon");
    }
  };

  getCouponRedemptions = async (
    couponId: number | string,
    limit = 20,
    offset = 0,
  ): Promise<{
    data: SwayyCouponRedemption[];
    pagination: { total: number; limit: number; offset: number };
  }> => {
    try {
      const response = (await getAPI(
        `/v1/cart/admin/coupons/${couponId}/redemptions`,
        { limit, offset },
      )) as SwayyAPIResponse<SwayyCouponRedemption[]> & {
        pagination?: { total: number; limit: number; offset: number };
      };
      assertSuccess(response);
      const rows = requireRowArray(
        response.data ?? response.result,
      ) as SwayyCouponRedemption[];

      return {
        data: rows,
        pagination: {
          total: response.pagination?.total ?? rows.length,
          limit: response.pagination?.limit ?? limit,
          offset: response.pagination?.offset ?? offset,
        },
      };
    } catch (error) {
      throw normalizeSwayyApiError(error, "Failed to fetch redemptions");
    }
  };

  getCouponActivityLogs = async (
    couponId: number | string,
    limit = 30,
    offset = 0,
  ): Promise<{
    data: SwayyCouponActivityLog[];
    pagination: { total: number; limit: number; offset: number };
  }> => {
    try {
      const response = (await getAPI(
        `/v1/cart/admin/coupons/${couponId}/activity-logs`,
        { limit, offset },
      )) as SwayyAPIResponse<SwayyCouponActivityLog[]> & {
        pagination?: { total: number; limit: number; offset: number };
      };
      assertSuccess(response);
      const rows = requireRowArray(
        response.data ?? response.result,
      ) as SwayyCouponActivityLog[];

      return {
        data: rows,
        pagination: {
          total: response.pagination?.total ?? rows.length,
          limit: response.pagination?.limit ?? limit,
          offset: response.pagination?.offset ?? offset,
        },
      };
    } catch (error) {
      throw normalizeSwayyApiError(error, "Failed to fetch coupon activity");
    }
  };

  createCoupon = async (
    body: CreateSwayyCouponPayload,
  ): Promise<SwayyCoupon> => {
    try {
      const response = (await postAPI(
        "/v1/cart/admin/coupons",
        body,
      )) as SwayyAPIResponse<SwayyCoupon>;
      return getResponseData(assertSuccess(response));
    } catch (error) {
      throw normalizeSwayyApiError(error, "Failed to create coupon");
    }
  };

  updateCoupon = async (
    couponId: number | string,
    body: UpdateSwayyCouponPayload,
  ): Promise<SwayyCoupon> => {
    try {
      const response = (await patchAPI(
        `/v1/cart/admin/coupons/${couponId}`,
        body,
      )) as SwayyAPIResponse<SwayyCoupon>;
      return getResponseData(assertSuccess(response));
    } catch (error) {
      throw normalizeSwayyApiError(error, "Failed to update coupon");
    }
  };

  expireCoupons = async (
    ids: number[],
  ): Promise<SwayyCouponBulkExpireResult> => {
    try {
      const response = (await postAPI(
        "/v1/cart/admin/coupons/bulk-expire",
        { ids },
      )) as SwayyAPIResponse<SwayyCouponBulkExpireResult>;
      return getResponseData(assertSuccess(response));
    } catch (error) {
      throw normalizeSwayyApiError(error, "Failed to expire coupons");
    }
  };
}
